import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomCheckoutSettingsApiV1CheckoutSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/checkout-settings',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/ecom/v1/checkout-settings',
        destPath: '/v1/checkout-settings',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/checkout-settings',
        destPath: '/v1/checkout-settings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_checkout-settings';

/**
 * Retrieves the sites' checkout settings.
 *
 *
 * The `getCheckoutSettings()` function returns a Promise that resolves to checkout settings.
 */
export function getCheckoutSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getCheckoutSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.checkout_settings.api.v1.CheckoutSettingsService.GetCheckoutSettings',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutSettingsApiV1CheckoutSettingsServiceUrl({
        protoPath: '/v1/checkout-settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'checkoutSettings.createdDate' },
              { path: 'checkoutSettings.updatedDate' },
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.x',
              },
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCheckoutSettings;
}

/**
 * Updates the sites' checkout settings.
 *
 *
 * The `updateCheckoutSettings()` function returns a Promise that resolves to the newly updated checkout settings.
 */
export function updateCheckoutSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCheckoutSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'checkoutSettings.createdDate' },
          { path: 'checkoutSettings.updatedDate' },
          {
            path: 'checkoutSettings.checkoutBrand.header.logo.image.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.x',
          },
          {
            path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.y',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.checkout_settings.api.v1.CheckoutSettingsService.UpdateCheckoutSettings',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutSettingsApiV1CheckoutSettingsServiceUrl({
        protoPath: '/v1/checkout-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'checkoutSettings.createdDate' },
              { path: 'checkoutSettings.updatedDate' },
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.x',
              },
              {
                path: 'checkoutSettings.checkoutBrand.header.logo.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCheckoutSettings;
}
